// A video's card: its thumbnail and whether this viewer can play it. Shared by the gallery
// (GalleryItem.vue) and the end-of-video recommendations (EndScreen.vue), 2026-10-14.
import {getTemporaryVideos, getVideoAccessNow} from "@/composables/videoAccessNow";
import {getFullstackUrl} from "@/composables/useFullstackUrl";

const FULLSTACK = getFullstackUrl();
const ACCESS_FIELDS = {masterclass: "masterclassVideosUntil", workshop: "workshopVideosUntil"};

// the video's pictures, through the fullstack's thumbnail proxy
export function getPictures(showcases, showcase, vimeoHash) {
  const pictures = showcases[showcase]?.["videos"]?.[vimeoHash]?.["pictures"];
  if (!pictures) return null;
  const origin = "https://i.vimeocdn.com/";
  const proxy = `${FULLSTACK}/api/v1/thumb/vimeo/`;
  return {...pictures, base_link: pictures.base_link.replace(origin, proxy)};
}

// A video's status on our sites (fullstack claude/VIDEOS.md, Seán 2026-10-21), from its Vimeo
// privacy and tag; as the fullstack's shared/videos/videoStatus.js. Vimeo's "public"
// ("anybody") covers both our Public and our Free.
//   public      anyone, no sign-in: "anybody" and tagged Showcase or Shorts
//   free        anyone signed in (the free years): "anybody"; older videos' "embed_only" too
//   restricted  accounts with access to its year (registering or buying): "disable"
//   unreleased  not on the site ("nobody"; the fullstack leaves these out of its files)
export const PUBLIC_TAGS = ["Showcase", "Shorts"];
export function getVideoStatus(video) {
  const privacy = video?.privacy;
  if (privacy === "nobody") return "unreleased";
  if (privacy === "anybody") return PUBLIC_TAGS.includes(video?.tag) ? "public" : "free";
  if (privacy === "embed_only") return "free";
  return "restricted";
}
export const getIsPublic = (video) => getVideoStatus(video) === "public";

// a picture at one of its sizes ("640x360"). Vimeo's base_link ends in "…-d?region=us_" (older
// ones "…-d_"): the size belongs before the query, "…-d_640x360?region=us". Appended to the end,
// it landed in the query, Vimeo ignored it and sent the full-size original: 1–2 MB per gallery
// thumbnail instead of 15–45 KB, and the /Videos gallery pulled ~100 MB (2026-10-18)
export function getThumbnailUrl(pictures, size) {
  const [path, query = ""] = pictures.base_link.split("?");
  const regionQuery = query.replace(/_$/, "");
  const sized = `${path.replace(/_$/, "")}_${size}`;
  return regionQuery ? `${sized}?${regionQuery}` : sized;
}

export function getSmallThumbnail(showcases, showcase, vimeoHash) {
  const pictures = getPictures(showcases, showcase, vimeoHash);
  if (!pictures) return;
  return getThumbnailUrl(pictures, pictures.sizes[2]);
}

// as the server decides (fullstack checkVideoAccess): signed in, and the account's access covers
// the video's year in its collection (or any of them: shared/videos/collectionOverlaps.json), or
// a valid temporary grant names the video (hash: its Vimeo hash, the showcase's key for it)
export function getHasVideoAccess({video, hash, showcase, user, isSignedIn}) {
  if (!isSignedIn) return false;
  if (video.privacy === "embed_only") return true;
  if (!user) return false;
  if (getTemporaryVideos(user.publicMetadata?.videoAccess).includes(hash)) return true;
  const videoAccess = getVideoAccessNow(user?.publicMetadata?.videoAccess);
  const year = parseInt(showcase.match(/\d+/)[0]);
  const ownType = /Masterclass/i.test(showcase) ? "masterclass" : "workshop";
  const collections = video.collections || [ownType];
  return collections.some((collection) => year <= (videoAccess[ACCESS_FIELDS[collection]] || 0));
}

// whether this viewer can watch the video now (2026-10-24, Seán): relative to the viewer, unlike
// the video's own status (public, free, restricted). A restricted video is locked or unlocked
// by the account's access.
export function getIsUnlocked({video, hash, showcase, user, isSignedIn}) {
  const status = getVideoStatus(video);
  if (status === "public") return true;
  if (status === "free") return Boolean(isSignedIn);
  return getHasVideoAccess({video, hash, showcase, user, isSignedIn});
}

// /Videos' two filters: "unlocked" (everything this viewer can watch) or "locked"
export const getAccessFilter = (access) => (getIsUnlocked(access) ? "unlocked" : "locked");

// the showcases holding only this viewer's videos of one filter category (viewer: {user,
// isSignedIn}); showcases left empty are dropped
export function getShowcasesInCategory(showcases, category, viewer) {
  const selected = {};
  for (const [showcase, record] of Object.entries(showcases)) {
    const entries = Object.entries(record.videos || {}).filter(([hash, video]) => {
      return getAccessFilter({video, hash, showcase, ...viewer}) === category;
    });
    if (entries.length) selected[showcase] = {...record, videos: Object.fromEntries(entries)};
  }
  return selected;
}
